/**
 * Navigation allowlist for Aria.
 *
 * SINGLE SOURCE OF TRUTH for every place the assistant is permitted to send a user. The realtime
 * `navigate` tool may ONLY target one of these route keys, and the frontend dispatcher resolves the
 * key → URL from this same map. There is no way for the model to navigate to an arbitrary or external
 * URL. Backend `AppAssistantActionHelper` re-exports this as `APP_ASSISTANT_ROUTES`.
 *
 * RULE: every entry must be reachable by a gamer or a facilitator. Admin-only surfaces (anything under
 * `/facilitator/back-office/user-management`) and dev tools never belong here — the assistant has no
 * admin audience, so sending a user there only bounces off route protection.
 * `transition-web-evolve/src/config/routes/__tests__/assistantRouteAllowlist.test.ts` enforces this.
 */

/**
 * The facilitator upcoming-activities page renders two views off one route. Shared so the allowlist
 * entry below and the page that reads the query string cannot drift apart.
 */
export const FACILITATOR_VIEW_PARAM = 'view'
export const FACILITATOR_CONTROL_TOWER_VIEW = 'control-tower'

export const ASSISTANT_ROUTE_ALLOWLIST = {
  privacy: '/privacy',
  gamerDashboard: '/gamer/dashboard-gamer',
  gamerFeedback: '/gamer/latest-session-feedback',
  gamerUpcoming: '/gamer/upcoming-activities',
  gamerGameCenter: '/gamer/game-center',
  gamerProfile: '/gamer/my-profile',
  // Part of the menu of a leader only.
  gamerMyPeople: '/gamer/my-people',
  gamerAccountSettings: '/gamer/account-settings',
  gamerFaq: '/gamer/faq',
  gamerTutorials: '/gamer/tutorial-videos',
  // The 1:1 coach page. `/gamer/ai-coach` is the team AI Coach activity — a different feature.
  gamerAriaCoach: '/gamer/coach',
  gamerAchievements: '/gamer/achievements',
  gamerTeamAnalysis: '/gamer/feedback/deeper-analysis/team',
  gamerIndividualAnalysis: '/gamer/feedback/deeper-analysis/individual',
  facilitatorDashboard: '/facilitator/dashboard-facilitator',
  facilitatorSprintCreation: '/facilitator/sprint-creation',
  facilitatorSprintManagement: '/facilitator/training-sprint-management',
  facilitatorUpcoming: '/facilitator/upcoming-activities',
  facilitatorControlTower: `/facilitator/upcoming-activities?${FACILITATOR_VIEW_PARAM}=${FACILITATOR_CONTROL_TOWER_VIEW}`,
  facilitatorGenerateReport: '/facilitator/generate-report',
  facilitatorReports: '/facilitator/manage-reports',
  facilitatorReflections: '/facilitator/manage-reflections',
  facilitatorCreateReflection: '/facilitator/facilitate-reflection/new',
  facilitatorProfile: '/facilitator/profile',
  facilitatorQa: '/facilitator/qa',
  facilitatorOrgMembers: '/facilitator/organization/members',
  facilitatorGameCenter: '/facilitator/game-center'
} as const

export type AssistantRouteKey = keyof typeof ASSISTANT_ROUTE_ALLOWLIST

export const ASSISTANT_ROUTE_KEYS = Object.keys(ASSISTANT_ROUTE_ALLOWLIST) as AssistantRouteKey[]

export function isAllowedAssistantRouteKey(key: string): key is AssistantRouteKey {
  return Object.prototype.hasOwnProperty.call(ASSISTANT_ROUTE_ALLOWLIST, key)
}

export function resolveAssistantRouteUrl(key: string): string | undefined {
  return isAllowedAssistantRouteKey(key) ? ASSISTANT_ROUTE_ALLOWLIST[key] : undefined
}

export const FACILITATOR_PATH_PREFIX = '/facilitator'
export const GAMER_PATH_PREFIX = '/gamer'

// Which shell a pathname belongs to. Only ever used to pick the greeting and the starter chips — the
// real role is resolved server-side from org membership and never inferred from a URL.
export function isFacilitatorPathname(pathname: string): boolean {
  return pathname.startsWith(FACILITATOR_PATH_PREFIX)
}

/** Gamer pages the assistant may float over: own results and what-is-next, never a feedback flow. */
const GAMER_ROUTES_WITH_ASSISTANT = [
  ASSISTANT_ROUTE_ALLOWLIST.gamerDashboard,
  ASSISTANT_ROUTE_ALLOWLIST.gamerUpcoming,
  ASSISTANT_ROUTE_ALLOWLIST.gamerFeedback,
  ASSISTANT_ROUTE_ALLOWLIST.gamerProfile,
  ASSISTANT_ROUTE_ALLOWLIST.gamerMyPeople
]

/**
 * Unity gameplay renders as a section inside BOTH shells, so these appear as `/gamer/play-game-*` and
 * `/facilitator/play-game-*`. Kept in step with `SharedRoutes.PLAY_GAME_*` by `assistantRouteAllowlist.test.ts`.
 */
export const GAMEPLAY_PATH_SEGMENTS = ['/play-game-with-introduction', '/play-game-without-introduction']

/** Gameplay pages, matched by segment because they render inside both shells. */
export function isGameplayRoute(pathname: string): boolean {
  return GAMEPLAY_PATH_SEGMENTS.some((segment) => pathname.includes(segment))
}

/** Flows whose footer carries the page's own controls; the fixed bubble lands on top of them. */
/** She may still navigate here — the reply naming the page is shown before she steps away. */
export const ASSISTANT_BLOCKED_PATH_SEGMENTS = ['/sprint-creation', '/sprint/', '/facilitate-reflection']

export function isAssistantAllowedOnRoute(pathname: string): boolean {
  if (ASSISTANT_BLOCKED_PATH_SEGMENTS.some((segment) => pathname.includes(segment))) return false
  // Available during gameplay; talking to Aria there is the user's own choice of when to mute.
  if (isGameplayRoute(pathname)) return true
  if (!pathname.startsWith(GAMER_PATH_PREFIX)) return true
  return GAMER_ROUTES_WITH_ASSISTANT.some((route) => pathname.startsWith(route))
}
